import {Button, Group, Tabs, Title} from '@mantine/core'
import {IconReport} from '@tabler/icons-react'
import {Link, Outlet, useNavigate, useRouterState} from '@tanstack/react-router'

import {InventoryExportMenu} from 'src/equipment-checks/inventory-export-menu'

type RentalAssetsTab = 'assets' | 'types' | 'companies' | 'offerings' | 'exceptions'

function isDetailRoute(pathname: string) {
    const segments = pathname.split('/').filter(Boolean)
    if (segments[0] !== 'rental-assets') {
        return false
    }

    if (segments[1] === 'stock') {
        return true
    }

    if (
        segments.length === 2 &&
        !['types', 'companies', 'offerings', 'exceptions'].includes(segments[1]!)
    ) {
        return true
    }

    return segments.length === 3 && segments[1] === 'types'
}

function activeTabFromPathname(pathname: string): RentalAssetsTab {
    if (pathname.startsWith('/rental-assets/types')) {
        return 'types'
    }
    if (pathname.startsWith('/rental-assets/companies')) {
        return 'companies'
    }
    if (pathname.startsWith('/rental-assets/offerings')) {
        return 'offerings'
    }
    if (pathname.startsWith('/rental-assets/exceptions')) {
        return 'exceptions'
    }
    return 'assets'
}

function tabPath(tab: RentalAssetsTab) {
    switch (tab) {
        case 'types':
            return '/rental-assets/types'
        case 'companies':
            return '/rental-assets/companies'
        case 'offerings':
            return '/rental-assets/offerings'
        case 'exceptions':
            return '/rental-assets/exceptions'
        default:
            return '/rental-assets'
    }
}

export function RentalAssetsLayout() {
    const pathname = useRouterState({select: (state) => state.location.pathname})
    const navigate = useNavigate()
    const activeTab = activeTabFromPathname(pathname)
    const isDetail = isDetailRoute(pathname)
    const showTabs = !isDetail

    return (
        <>
            {!isDetail ? (
                <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm" mb="md">
                    <Title order={1}>Equipment</Title>
                    <Group gap="sm">
                        <InventoryExportMenu />
                        <Button
                            component={Link}
                            to="/reports/rental"
                            variant="default"
                            leftSection={<IconReport size={16} stroke={1.5} />}
                        >
                            Rental Report
                        </Button>
                    </Group>
                </Group>
            ) : null}

            {showTabs && (
                <Tabs
                    mb="md"
                    value={activeTab}
                    onChange={(value) => {
                        if (value) {
                            void navigate({to: tabPath(value as RentalAssetsTab)})
                        }
                    }}
                >
                    <Tabs.List>
                        <Tabs.Tab value="assets">Assets</Tabs.Tab>
                        <Tabs.Tab value="types">Types</Tabs.Tab>
                        <Tabs.Tab value="companies">Third-Party Rental Companies</Tabs.Tab>
                        <Tabs.Tab value="offerings">Third-Party Rental Offerings</Tabs.Tab>
                        <Tabs.Tab value="exceptions">Exceptions</Tabs.Tab>
                    </Tabs.List>
                </Tabs>
            )}

            <Outlet />
        </>
    )
}
